import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBolt,
  faCircleArrowUp,
  faPaperPlane,
  faPhone,
  faUserGroup,
} from "@fortawesome/free-solid-svg-icons";
import useNewCommonAPI from "../../api/commonAPI";
import useNewPhoneNumberAPI from "../../api/phoneNumberAPI";
import useNewS2SAPI from "../../api/s2sAPI";
import PhoneConfirmationPopup from "../../../components/PhoneConfirmationPopup";
import { ACTION_TYPES } from "../../../app/actionTypes";
import {
  setActionPayload,
  setActionType,
  setValidationInfo,
} from "../../../features/authorizationSlice";
import { TYPE_IDS } from "../../../app/typeIds";
import { formatAccount } from "../../../features/ValueFormater";
import { validationTypes } from "../../../utils/Constants";
import S2SAside from "../S2SAside";
import FileUpload from "../Common/FileUpload";
import AppAmountInput from "../Common/AmountInput";
import FormInput from "../Common/FormInput";
import ExpiryDaysInput, {
  EXPIRY_DAYS_REQUIRED_MESSAGE,
  validateExpiryDays,
  withExpiryDaysPayload,
} from "../Common/ExpiryDaysInput";
import CurrencyRadioGroup from "../Common/CurrencyRadioGroup";
import AppButton from "../Common/Button";
import { CURRENCY_IDS, S2S_PRESET_AMOUNTS } from "../../constants/currency";
import { ACCOUNT_TYPE_IDS } from "../../constants/accountTypes";
import { SESSION_STORAGE_KEYS } from "../../constants/sessionStorage";
import {
  getPhoneValidationMessage,
  normalizePhoneForValidation,
} from "../../utils/phoneValidation";

function readFileAsBase64(file, onLoad) {
  const reader = new FileReader();
  reader.readAsDataURL(file);
  reader.onloadend = () => {
    onLoad(reader.result);
  };
}

function triggerFileDownload(href) {
  if (!href) return;

  const link = document.createElement("a");
  link.href = href;
  link.target = "_blank";
  link.rel = "noreferrer";
  link.download = "";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

function formatAmountDisplay(amount, currencyId) {
  if (amount === null || amount === undefined || amount === "") return "";
  const normalizedAmount = String(amount).replace(/[^\d.]/g, "");
  if (!normalizedAmount) return "";
  const [integerPart = "", decimalPart = ""] = normalizedAmount.split(".");
  const hasTrailingDot = normalizedAmount.endsWith(".");
  const formattedInteger = (integerPart || "0").replace(
    /\B(?=(\d{3})+(?!\d))/g,
    ","
  );

  if (Number(currencyId) === CURRENCY_IDS.USD) {
    if (decimalPart.length > 0 || hasTrailingDot) {
      return `${formattedInteger}.${decimalPart.slice(0, 2)}`;
    }
  }

  return formattedInteger;
}

function normalizeAmountValue(value, currencyId) {
  const numericValue = String(value || "").replace(/[^\d.]/g, "");
  const noLeadingZeroes = numericValue.replace(/^0+(?=\d)/, "");
  const parts = noLeadingZeroes.split(".");

  return Number(currencyId) === CURRENCY_IDS.USD
    ? parts.length > 1
      ? `${parts[0]}.${parts[1].slice(0, 2)}`
      : parts[0]
    : parts[0];
}

function ComposerCard({ icon, subtitle, title, children }) {
  return (
    <div className="new-s2s-composer__main">
      <div className="new-s2s-composer__hero">
        <span className="new-s2s-composer__hero-icon" aria-hidden="true">
          <FontAwesomeIcon icon={icon} />
        </span>
        <div>
          <h2>{title}</h2>
          <p>{subtitle}</p>
        </div>
      </div>
      <div className="new-s2s-composer__card">{children}</div>
    </div>
  );
}

function ComposerSplit({ children, aside }) {
  return (
    <div className="new-s2s-composer__split">
      <div>{children}</div>
      {aside ? (
        <aside className="new-s2s-composer__aside">{aside}</aside>
      ) : null}
    </div>
  );
}

function FormField({
  label,
  hint,
  error,
  required = false,
  trailing,
  children,
}) {
  return (
    <div className="new-s2s-form__group">
      <div className="new-s2s-form__label-row">
        <label className="new-s2s-form__label">
          {label}
          {required ? " *" : ""}
        </label>
        {trailing}
      </div>
      {children}
      {hint ? <p className="new-s2s-form__hint">{hint}</p> : null}
      {error ? <p className="new-s2s-form__error">{error}</p> : null}
    </div>
  );
}

function TransferAmountField({
  currencyId,
  value,
  onChange,
  onPresetSelect,
  error,
}) {
  const prefix = Number(currencyId) === CURRENCY_IDS.USD ? "$" : "L.L";
  const formattedValue = formatAmountDisplay(value, currencyId);
  const presetAmounts =
    S2S_PRESET_AMOUNTS[Number(currencyId)] ||
    S2S_PRESET_AMOUNTS[CURRENCY_IDS.USD];

  return (
    <FormField
      label={`Amount (${
        Number(currencyId) === CURRENCY_IDS.USD ? "USD" : "LBP"
      })`}
      required
      hint="No transfer fees apply"
      error={error}
      trailing={
        <div className="new-s2s-form__chips">
          {presetAmounts.map((presetAmount) => (
            <button
              key={presetAmount}
              type="button"
              className="new-s2s-form__chip"
              onClick={() => onPresetSelect(String(presetAmount))}
            >
              {presetAmount.toLocaleString("en-US")}
            </button>
          ))}
        </div>
      }
    >
      <AppAmountInput
        label=""
        aria-invalid={Boolean(error)}
        prefix={prefix}
        value={formattedValue}
        placeholder={Number(currencyId) === CURRENCY_IDS.USD ? "0.00" : "0"}
        formatDraftValue={(nextRaw) => formatAmountDisplay(nextRaw, currencyId)}
        onChange={(event) =>
          onChange(normalizeAmountValue(event.target.value, currencyId))
        }
      />
    </FormField>
  );
}

export const SUYOOL_LOOKUP_MESSAGES = {
  validUser: "Phone Number entered is a valid suyool user",
  notSuyoolUser: "Phone Number entered is not a suyool user",
  notCorporateUser: "Phone Number entered is not a corporate user",
};

function normalizeRecipientSuggestions(response) {
  const payload = response?.Payload || response?.payload || response;
  const suggestions =
    payload?.Suggestions ||
    payload?.suggestions ||
    payload?.Recipients ||
    payload?.recipients ||
    payload?.Users ||
    payload?.users ||
    payload?.Corporates ||
    payload?.corporates ||
    payload;

  return Array.isArray(suggestions) ? suggestions : [];
}

export function useRecipientNameLookup(typeId) {
  const { GetUserNameByMobileNo, setUserName, userName } = useNewCommonAPI();
  const { validateNumberBack } = useNewPhoneNumberAPI();
  const [resolvedName, setResolvedName] = useState("");
  const getUserNameRef = useRef(GetUserNameByMobileNo);

  getUserNameRef.current = GetUserNameByMobileNo;

  useEffect(() => {
    setResolvedName(userName || "");
  }, [userName]);

  useEffect(() => {
    setUserName("");
    setResolvedName("");
  }, [setUserName, typeId]);

  const checkPhoneNumber = useCallback(
    async (filteredValue) => {
      setResolvedName("");

      if (!filteredValue) return null;

      const isPotentialLookupNumber =
        /^\d*$/.test(filteredValue) &&
        filteredValue.length <= 12 &&
        !filteredValue.startsWith("9610");

      if (isPotentialLookupNumber) {
        getUserNameRef.current(filteredValue, typeId).catch(() => {});
      }

      try {
        const response = await validateNumberBack(
          normalizePhoneForValidation(filteredValue),
          validationTypes.s2s,
          typeId,
        );

        return {
          valid: true,
          message: getPhoneValidationMessage(response),
        };
      } catch (error) {
        return {
          valid: false,
          message: getPhoneValidationMessage(error),
        };
      }
    },
    [typeId, validateNumberBack]
  );

  return { resolvedName, checkPhoneNumber };
}

function RecipientLookupField({
  payload,
  setPayload,
  errors,
  setErrors,
  isIndividual,
  confirmNumber,
  setConfirmNumber,
  typeId,
}) {
  const { resolvedName, checkPhoneNumber } = useRecipientNameLookup(typeId);
  const { GetS2STransferSuggestions } = useNewS2SAPI();
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const autocompleteRef = useRef(null);
  const requestRef = useRef(0);
  const suggestionsApiRef = useRef(GetS2STransferSuggestions);
  const phoneLookupRef = useRef(checkPhoneNumber);

  suggestionsApiRef.current = GetS2STransferSuggestions;
  phoneLookupRef.current = checkPhoneNumber;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        autocompleteRef.current &&
        !autocompleteRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const query = String(payload.PhoneNo || "").trim();
    requestRef.current += 1;
    const requestId = requestRef.current;
    const isNameSearch = /[^\d+\s]/.test(query);

    if (payload.RecipientName || query.length < 3) {
      setSuggestions([]);
      setShowSuggestions(false);
      setLoadingSuggestions(false);
      return undefined;
    }

    setLoadingSuggestions(true);
    const debounceId = window.setTimeout(async () => {
      try {
        const phoneValidationPromise = isNameSearch
          ? Promise.resolve(null)
          : phoneLookupRef.current(query);
        const [suggestionsResult, phoneValidationResult] =
          await Promise.allSettled([
            suggestionsApiRef.current(query, typeId),
            phoneValidationPromise,
          ]);
        if (requestRef.current !== requestId) return;
        const list =
          suggestionsResult.status === "fulfilled"
            ? normalizeRecipientSuggestions(suggestionsResult.value)
            : [];
        const result =
          phoneValidationResult.status === "fulfilled"
            ? phoneValidationResult.value
            : {
                valid: false,
                message: getPhoneValidationMessage(
                  phoneValidationResult.reason,
                ),
              };
        setSuggestions(list);

        const applyPhoneValidation = () => {
          if (!result?.valid) {
            setConfirmNumber(false);
            setErrors((currentErrors) => ({
              ...currentErrors,
              PhoneNo: result?.message || "Phone number is invalid.",
            }));
            return;
          }

          if (result.message === SUYOOL_LOOKUP_MESSAGES.validUser) {
            if (isIndividual) setConfirmNumber(false);
            setErrors((currentErrors) => ({
              ...currentErrors,
              PhoneNo: "",
            }));
            return;
          }

          if (
            result.message === SUYOOL_LOOKUP_MESSAGES.notSuyoolUser &&
            isIndividual
          ) {
            setErrors((currentErrors) => ({
              ...currentErrors,
              PhoneNo: "",
            }));
            setConfirmNumber(true);
            return;
          }

          setConfirmNumber(false);
          setErrors((currentErrors) => ({
            ...currentErrors,
            PhoneNo: result.message || "Phone number is invalid.",
          }));
        };

        if (list.length > 0) {
          setShowSuggestions(true);
          applyPhoneValidation();
          return;
        }

        if (isNameSearch) {
          setShowSuggestions(true);
          setConfirmNumber(false);
          setErrors((currentErrors) => ({
            ...currentErrors,
            PhoneNo: "",
          }));
          return;
        }

        setShowSuggestions(false);
        applyPhoneValidation();
      } catch {
        if (requestRef.current !== requestId) return;
        setSuggestions([]);
        setShowSuggestions(false);
        if (isNameSearch) {
          setErrors((currentErrors) => ({ ...currentErrors, PhoneNo: "" }));
        }
      } finally {
        if (requestRef.current === requestId) {
          setLoadingSuggestions(false);
        }
      }
    }, 300);

    return () => window.clearTimeout(debounceId);
  }, [
    isIndividual,
    payload.PhoneNo,
    payload.RecipientName,
    setConfirmNumber,
    setErrors,
    typeId,
  ]);

  useEffect(() => {
    setSuggestions([]);
    setShowSuggestions(false);
    setLoadingSuggestions(false);
  }, [typeId]);

  const getSuggestionName = (suggestion) =>
    suggestion?.Name ||
    suggestion?.FullName ||
    suggestion?.CorporateOfficialName ||
    suggestion?.OfficialName ||
    suggestion?.CorporateName ||
    "-";

  const getSuggestionPhone = (suggestion) =>
    suggestion?.PhoneNumber ||
    suggestion?.PhoneNo ||
    suggestion?.MobileNo ||
    suggestion?.MobileNumber ||
    "";

  const handlePhoneChange = (event) => {
    const { value } = event.target;
    const hasNameCharacters = /[^\d+\s]/.test(value);
    const filteredValue = hasNameCharacters ? value : value.replace(/\s/g, "");

    setPayload((currentPayload) => ({
      ...currentPayload,
      PhoneNo: filteredValue,
      RecipientName: "",
    }));

    setConfirmNumber(false);
    setErrors((currentErrors) => ({ ...currentErrors, PhoneNo: "" }));
  };

  const handleSelectSuggestion = (suggestion) => {
    const nextPhone = getSuggestionPhone(suggestion);
    const nextName = getSuggestionName(suggestion);

    setPayload((currentPayload) => ({
      ...currentPayload,
      PhoneNo: nextPhone,
      RecipientName: nextName,
    }));
    setConfirmNumber(false);
    setErrors((currentErrors) => ({ ...currentErrors, PhoneNo: "" }));
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const displayName = confirmNumber
    ? "The user is not on Suyool"
    : payload.RecipientName ||
      resolvedName ||
      "Recipient name will appear here";
  const placeholder = isIndividual
    ? "Phone number or Full name"
    : "Phone number or Corporate official name";
  const nameLabel = isIndividual ? "Recipient Name" : "Corporate Name";

  return (
    <div className="new-s2s-form__recipient-grid">
      <FormField
        label={isIndividual ? "Recipient" : "Corporate / Business"}
        required
        hint={
          isIndividual
            ? "Search by phone number or full name."
            : "Search by phone number or corporate official name."
        }
        error={errors.PhoneNo}
      >
        <div className="new-s2s-form__autocomplete" ref={autocompleteRef}>
          <FormInput
            type="text"
            name="PhoneNo"
            aria-invalid={Boolean(errors.PhoneNo)}
            value={payload.PhoneNo}
            placeholder={placeholder}
            autoComplete="off"
            prefix={<FontAwesomeIcon icon={faPhone} />}
            onFocus={() => {
              if (suggestions.length > 0) setShowSuggestions(true);
            }}
            onChange={handlePhoneChange}
          />

          {showSuggestions || loadingSuggestions ? (
            <div className="new-s2s-form__suggestions">
              {loadingSuggestions ? (
                <div className="new-s2s-form__suggestion-empty">
                  Searching...
                </div>
              ) : suggestions.length > 0 ? (
                suggestions.map((suggestion, index) => {
                  const name = getSuggestionName(suggestion);
                  const phone = getSuggestionPhone(suggestion);

                  return (
                    <button
                      key={`${phone || name}-${index}`}
                      type="button"
                      className="new-s2s-form__suggestion"
                      onMouseDown={(event) => {
                        event.preventDefault();
                        handleSelectSuggestion(suggestion);
                      }}
                    >
                      <span className="new-s2s-form__suggestion-avatar">
                        {String(name || "?")
                          .charAt(0)
                          .toUpperCase()}
                      </span>
                      <span className="new-s2s-form__suggestion-copy">
                        <strong>{name}</strong>
                        <small>{phone || "-"}</small>
                      </span>
                    </button>
                  );
                })
              ) : (
                <div className="new-s2s-form__suggestion-empty">
                  No matches found.
                </div>
              )}
            </div>
          ) : null}
        </div>
      </FormField>

      <FormField label={nameLabel}>
        <div
          className={`new-s2s-form__readonly ${
            confirmNumber ? "is-warning" : ""
          }`}
        >
          <FontAwesomeIcon icon={faUserGroup} />
          <span>{displayName}</span>
        </div>
      </FormField>
    </div>
  );
}

function FileUploadField({
  label,
  hint,
  accept,
  selectedFile,
  error,
  downloadSampleLabel,
  onDownloadSample,
  onSelect,
  onClearError,
}) {
  return (
    <div className="new-s2s-form__group">
      <FileUpload
        label={label}
        description={hint || "PDF, image, or spreadsheet files up to 2MB"}
        accept={accept}
        maxFileSize={2 * 1024 * 1024}
        selectedFile={selectedFile}
        error={error}
        downloadSampleLabel={downloadSampleLabel}
        onDownloadSample={onDownloadSample}
        onValidationError={onClearError}
        onFileChange={onSelect}
      />
    </div>
  );
}

// The Download Excel template control must always stay clickable - it must
// never be gated by transfer/table status (Pending/Approved/Rejected) or by
// the transfers list loading state. If the template URL hasn't been fetched
// yet (or the initial fetch failed), clicking re-fetches it on demand.
function useS2STemplateHref() {
  const { GetS2STransferConfig } = useNewS2SAPI();
  const getS2STransferConfigRef = useRef(GetS2STransferConfig);
  const isFetchingRef = useRef(false);
  const isMountedRef = useRef(true);
  const [templateHref, setTemplateHref] = useState(
    () =>
      sessionStorage.getItem(SESSION_STORAGE_KEYS.S2S_TRANSFER_TEMPLATE) || ""
  );
  const [templateLoading, setTemplateLoading] = useState(false);

  getS2STransferConfigRef.current = GetS2STransferConfig;

  useEffect(
    () => () => {
      isMountedRef.current = false;
    },
    []
  );

  const fetchTemplateHref = useCallback(async () => {
    isFetchingRef.current = true;
    if (isMountedRef.current) setTemplateLoading(true);

    try {
      const response = await getS2STransferConfigRef.current();
      const nextTemplateHref =
        response?.Payload?.TemplateURL ||
        sessionStorage.getItem(SESSION_STORAGE_KEYS.S2S_TRANSFER_TEMPLATE) ||
        "";

      if (isMountedRef.current) setTemplateHref(nextTemplateHref);
      return nextTemplateHref;
    } catch {
      const fallbackHref =
        sessionStorage.getItem(SESSION_STORAGE_KEYS.S2S_TRANSFER_TEMPLATE) ||
        "";
      if (isMountedRef.current) setTemplateHref(fallbackHref);
      return fallbackHref;
    } finally {
      isFetchingRef.current = false;
      if (isMountedRef.current) setTemplateLoading(false);
    }
  }, []);

  const downloadTemplate = useCallback(async () => {
    if (templateHref) {
      triggerFileDownload(templateHref);
      return;
    }

    if (isFetchingRef.current) return;

    const href = await fetchTemplateHref();
    triggerFileDownload(href);
  }, [fetchTemplateHref, templateHref]);

  useEffect(() => {
    void fetchTemplateHref();
  }, [fetchTemplateHref]);

  return { templateHref, templateLoading, downloadTemplate };
}

function InstantTransferBanner() {
  return (
    <div className="new-s2s-form__banner">
      <span className="new-s2s-form__banner-icon" aria-hidden="true">
        <FontAwesomeIcon icon={faBolt} />
      </span>
      <div>
        <strong>Instant Transfer</strong>
        <p>
          Funds will be transferred instantly to the recipient&apos;s account
          with zero fees.
        </p>
      </div>
    </div>
  );
}

function getNewTransferInitialPayload(initialValues = {}) {
  return {
    CurrencyID: Number(initialValues?.currencyId) || CURRENCY_IDS.USD,
    PhoneNo: String(initialValues?.phoneNumber || ""),
    Amount: String(initialValues?.amount || ""),
    ReasonOfTransfer: "",
    ReasonFileName: "",
    ReasonFile: null,
    FeesAmount: 0,
    ProcessingFeesOnCorporate: true,
    DaysToExpire: "",
    RecipientName: "",
  };
}

export function NewTransferComposer({ initialValues, onOpenQrAfterGenerate }) {
  const dispatch = useDispatch();
  const initialAmount = initialValues?.amount;
  const initialCurrencyId = initialValues?.currencyId;
  const initialPhoneNumber = initialValues?.phoneNumber;
  const initialRecipientType = initialValues?.recipientType;
  const hasInitialValues = Boolean(initialValues);
  const [isIndividual, setIsIndividual] = useState(
    () => initialRecipientType !== "business"
  );
  const [confirmNumber, setConfirmNumber] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [payload, setPayload] = useState(() =>
    getNewTransferInitialPayload({
      amount: initialAmount,
      currencyId: initialCurrencyId,
      phoneNumber: initialPhoneNumber,
      recipientType: initialRecipientType,
    })
  );
  const [errors, setErrors] = useState({});
  const isExpiryRequired = isIndividual && confirmNumber;
  const recipientTypeId = isIndividual
    ? ACCOUNT_TYPE_IDS.INDIVIDUAL
    : ACCOUNT_TYPE_IDS.CORPORATE;

  useEffect(() => {
    if (isExpiryRequired) return;
    setErrors((currentErrors) =>
      currentErrors.DaysToExpire === EXPIRY_DAYS_REQUIRED_MESSAGE
        ? { ...currentErrors, DaysToExpire: "" }
        : currentErrors
    );
  }, [isExpiryRequired]);

  useEffect(() => {
    if (!hasInitialValues) return;

    setIsIndividual(initialRecipientType !== "business");
    setConfirmNumber(false);
    setPayload((currentPayload) => ({
      ...currentPayload,
      CurrencyID: Number(initialCurrencyId) || CURRENCY_IDS.USD,
      PhoneNo: String(initialPhoneNumber || ""),
      Amount: String(initialAmount || ""),
    }));
    setErrors({});
  }, [
    hasInitialValues,
    initialAmount,
    initialCurrencyId,
    initialPhoneNumber,
    initialRecipientType,
  ]);

  useEffect(() => {
    if (!selectedFile) return;

    readFileAsBase64(selectedFile, (base64String) => {
      setPayload((currentPayload) => ({
        ...currentPayload,
        ReasonFileName: selectedFile.name,
        ReasonFile: base64String,
      }));
    });
  }, [selectedFile]);

  const validateForm = () => {
    let isValid = true;
    const newErrors = {};

    if (!payload.CurrencyID || payload.CurrencyID === "0") {
      newErrors.CurrencyID = "Account is required.";
      isValid = false;
    }

    if (isIndividual && errors.PhoneNo) {
      isValid = false;
    }

    if (!payload.PhoneNo) {
      newErrors.PhoneNo = "Phone Number is required.";
      isValid = false;
    } else if (/[^\d+\s]/.test(payload.PhoneNo) && !payload.RecipientName) {
      newErrors.PhoneNo = "Please select a recipient from the list.";
      isValid = false;
    }

    if (!payload.Amount) {
      newErrors.Amount = "Amount is required.";
      isValid = false;
    } else if (isNaN(payload.Amount)) {
      newErrors.Amount = "Amount must be a number.";
      isValid = false;
    } else if (Number(payload.Amount) <= 0) {
      newErrors.Amount = "Amount must be a positive number.";
      isValid = false;
    }

    if (!payload.ReasonOfTransfer) {
      newErrors.ReasonOfTransfer = "Reason is required.";
      isValid = false;
    }

    const expiryDaysError = validateExpiryDays(payload.DaysToExpire, {
      required: isExpiryRequired,
    });
    if (expiryDaysError) {
      newErrors.DaysToExpire = expiryDaysError;
      isValid = false;
    }

    const fileSizeLimit = 2 * 1024 * 1024;
    if (selectedFile && selectedFile.size > fileSizeLimit) {
      newErrors.ReasonFile = "File should not be larger than 2MB.";
      isValid = false;
    }

    if (errors.PhoneNo) {
      setErrors({ ...newErrors, PhoneNo: errors.PhoneNo });
    } else {
      setErrors(newErrors);
    }

    return isValid;
  };

  const handleSubmit = () => {
    const { RecipientName, ...payloadForSubmit } = payload;

    dispatch(setActionType(ACTION_TYPES.PUSH_S2S_TRANSFER));
    dispatch(
      setValidationInfo({
        title: "Payment Validation Needed",
        profile: {
          label: "Receiver Name",
          mobileNo: payload.PhoneNo,
          accountTypeId: recipientTypeId,
          icon: "s2s_popup.svg",
        },
        listings: [
          {
            label: "Amount",
            value: formatAccount(payload.Amount, Number(payload.CurrencyID)),
          },
          {
            label: "Reason",
            value: payload.ReasonOfTransfer,
          },
        ],
        document: {
          documentUrl: payload.ReasonFile,
          documentName: payload.ReasonFileName,
        },
      })
    );

    const filteredPayload = withExpiryDaysPayload(
      isIndividual ? payloadForSubmit : { ...payloadForSubmit, FeesAmount: 0 }
    );

    dispatch(setActionPayload(filteredPayload));
    onOpenQrAfterGenerate?.({
      typeId: TYPE_IDS.PUSH_S2S_TRANSFER,
      additionalData: {
        amount: Number(payload.Amount),
        currency: payload.CurrencyID === CURRENCY_IDS.USD ? "USD" : "LBP",
        phoneNumber: payload.PhoneNo,
      },
    });
  };

  const handleConfirm = (event) => {
    event.preventDefault();

    if (!validateForm()) return;
    if (confirmNumber) {
      setModalOpen(true);
      return;
    }

    handleSubmit();
  };

  const handleReceiverTypeChange = (value) => {
    const isNextIndividual = value === "individual";
    setIsIndividual(isNextIndividual);
    setConfirmNumber(false);
    setPayload((currentPayload) => ({
      ...currentPayload,
      PhoneNo: "",
      RecipientName: "",
      ReasonFileName: "",
      ReasonFile: null,
      ReasonOfTransfer: "",
      DaysToExpire: "",
    }));
    setSelectedFile(null);
    setErrors((currentErrors) => ({
      ...currentErrors,
      PhoneNo: "",
      ReasonFileName: "",
      ReasonFile: "",
      ReasonOfTransfer: "",
      DaysToExpire: "",
    }));
  };

  const handleFeesRadioChange = (event) => {
    const { id } = event.target;
    setPayload((currentPayload) => ({
      ...currentPayload,
      ProcessingFeesOnCorporate: id === "onUs",
    }));
  };

  return (
    <>
      <div className="new-s2s-composer">
        <ComposerSplit aside={<S2SAside />}>
          <ComposerCard
            icon={faPaperPlane}
            title="Suyool to Suyool Transfer"
            subtitle="Instant, free transfers between Suyool accounts"
          >
            <form
              className="new-s2s-form"
              onSubmit={handleConfirm}
              autoComplete="off"
            >
              <CurrencyRadioGroup
                label="From Account"
                name="currency"
                value={String(payload.CurrencyID)}
                onChange={(value) =>
                  setPayload((currentPayload) => ({
                    ...currentPayload,
                    CurrencyID: Number(value),
                  }))
                }
                options={[
                  { value: String(CURRENCY_IDS.USD), label: "USD" },
                  { value: String(CURRENCY_IDS.LBP), label: "LBP" },
                ]}
              />

              <TransferAmountField
                currencyId={payload.CurrencyID}
                value={payload.Amount}
                error={errors.Amount}
                onChange={(nextValue) =>
                  setPayload((currentPayload) => ({
                    ...currentPayload,
                    Amount: nextValue,
                  }))
                }
                onPresetSelect={(presetValue) =>
                  setPayload((currentPayload) => ({
                    ...currentPayload,
                    Amount: presetValue,
                  }))
                }
              />

              <CurrencyRadioGroup
                label="Recipient Type"
                name="recipientType"
                value={isIndividual ? "individual" : "business"}
                onChange={handleReceiverTypeChange}
                options={[
                  { value: "individual", label: "Individual" },
                  { value: "business", label: "Business" },
                ]}
              />

              <RecipientLookupField
                payload={payload}
                setPayload={setPayload}
                errors={errors}
                setErrors={setErrors}
                isIndividual={isIndividual}
                confirmNumber={confirmNumber}
                setConfirmNumber={setConfirmNumber}
                typeId={recipientTypeId}
              />

              <FormField
                label="Reason / Note"
                required
                hint="Add a clear reason for this transfer."
                error={errors.ReasonOfTransfer}
              >
                <FormInput
                  as="textarea"
                  rows={3}
                  name="ReasonOfTransfer"
                  aria-invalid={Boolean(errors.ReasonOfTransfer)}
                  value={payload.ReasonOfTransfer}
                  placeholder="Add a message for the recipient"
                  onChange={(event) => {
                    const { value } = event.target;
                    setPayload((currentPayload) => ({
                      ...currentPayload,
                      ReasonOfTransfer: value,
                    }));
                    setErrors((currentErrors) => ({
                      ...currentErrors,
                      ReasonOfTransfer: "",
                    }));
                  }}
                />
              </FormField>

              <ExpiryDaysInput
                required={isExpiryRequired}
                value={payload.DaysToExpire}
                error={errors.DaysToExpire}
                onChange={(nextValue) =>
                  setPayload((currentPayload) => ({
                    ...currentPayload,
                    DaysToExpire: nextValue,
                  }))
                }
                onClearError={() =>
                  setErrors((currentErrors) => ({
                    ...currentErrors,
                    DaysToExpire: "",
                  }))
                }
              />

              <FileUploadField
                label="Upload File"
                hint="Attach a supporting document if needed."
                selectedFile={selectedFile}
                error={errors.ReasonFile}
                onClearError={() =>
                  setErrors((currentErrors) => ({
                    ...currentErrors,
                    ReasonFile: "",
                  }))
                }
                onSelect={setSelectedFile}
              />

              <InstantTransferBanner />

              <AppButton type="submit" className="new-s2s-form__submit">
                Continue
              </AppButton>
            </form>
          </ComposerCard>
        </ComposerSplit>
      </div>

      <PhoneConfirmationPopup
        isOpen={modalOpen}
        setIsOpen={setModalOpen}
        onCorporate={payload.ProcessingFeesOnCorporate}
        handleSubmit={handleSubmit}
        numberToConfirm={payload.PhoneNo}
        amount={Number(payload.Amount)}
        currencyId={payload.CurrencyID}
        handleFeesRadioChange={handleFeesRadioChange}
        setPayload={setPayload}
        isIndividual={isIndividual}
        dialogClassName="new-s2s-confirmation-modal"
      />
    </>
  );
}

export function useBulkTransferForm({ onOpenQrAfterGenerate } = {}) {
  const dispatch = useDispatch();
  const { getAllCurrencies } = useNewCommonAPI();
  const { templateHref, downloadTemplate } = useS2STemplateHref();
  const getAllCurrenciesRef = useRef(getAllCurrencies);
  const [selectedFile, setSelectedFile] = useState(new File([], ""));
  const [payload, setPayload] = useState({
    CurrencyID: CURRENCY_IDS.USD,
    ReasonFileName: "",
    formFile: null,
    DaysToExpire: "",
  });
  const [errors, setErrors] = useState({});

  getAllCurrenciesRef.current = getAllCurrencies;

  useEffect(() => {
    getAllCurrenciesRef.current();
  }, []);

  useEffect(() => {
    if (!selectedFile) return;
    readFileAsBase64(selectedFile, (base64String) => {
      setPayload((currentPayload) => ({
        ...currentPayload,
        ReasonFileName: selectedFile.name,
        formFile: base64String,
      }));
    });
  }, [selectedFile]);

  const updateCurrency = (value) => {
    setPayload((currentPayload) => ({
      ...currentPayload,
      CurrencyID: Number(value),
    }));
  };

  const clearFileError = () => {
    setErrors((currentErrors) => ({
      ...currentErrors,
      formFile: "",
    }));
  };

  const validateForm = () => {
    let isValid = true;
    const newErrors = {};

    if (!payload.CurrencyID) {
      newErrors.CurrencyID = "Account is required.";
      isValid = false;
    }

    if (
      selectedFile === undefined ||
      selectedFile?.name === undefined ||
      selectedFile?.size <= 0
    ) {
      newErrors.formFile = "File upload is required.";
      isValid = false;
    } else if (!/\.xlsx?$/.test(selectedFile.name.toLowerCase())) {
      newErrors.formFile = "Only Excel files are allowed.";
      isValid = false;
    }

    const fileSizeLimit = 2 * 1024 * 1024;
    if (selectedFile && selectedFile.size > fileSizeLimit) {
      newErrors.formFile = "File should not be larger than 2MB.";
      isValid = false;
    }

    const expiryDaysError = validateExpiryDays(payload.DaysToExpire, {
      required: true,
    });
    if (expiryDaysError) {
      newErrors.DaysToExpire = expiryDaysError;
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = (event) => {
    event?.preventDefault?.();

    if (!validateForm()) return false;

    dispatch(setActionType(ACTION_TYPES.PUSH_BULK_S2S_TRANSFER));
    dispatch(setActionPayload(withExpiryDaysPayload(payload)));
    onOpenQrAfterGenerate?.();
    return true;
  };

  return {
    payload,
    selectedFile,
    setSelectedFile,
    errors,
    templateHref,
    downloadTemplate,
    updateCurrency,
    clearFileError,
    setPayload,
    setErrors,
    handleSubmit,
  };
}

export function BulkTransferComposer({ onOpenQrAfterGenerate }) {
  const {
    payload,
    selectedFile,
    setSelectedFile,
    errors,
    downloadTemplate,
    updateCurrency,
    clearFileError,
    setPayload,
    setErrors,
    handleSubmit,
  } = useBulkTransferForm({ onOpenQrAfterGenerate });

  return (
    <div className="new-s2s-composer">
      <ComposerSplit aside={<S2SAside />}>
        <ComposerCard
          icon={faUserGroup}
          title="Bulk Suyool Transfers"
          subtitle="Upload one file to transfer money to two or more recipients"
        >
          <form
            className="new-s2s-form"
            onSubmit={handleSubmit}
            autoComplete="off"
          >
            <CurrencyRadioGroup
              label="From Account"
              name="bulkCurrency"
              value={String(payload.CurrencyID)}
              onChange={updateCurrency}
              options={[
                { value: String(CURRENCY_IDS.USD), label: "USD" },
                { value: String(CURRENCY_IDS.LBP), label: "LBP" },
              ]}
            />

            <ExpiryDaysInput
              required
              value={payload.DaysToExpire}
              error={errors.DaysToExpire}
              onChange={(nextValue) =>
                setPayload((currentPayload) => ({
                  ...currentPayload,
                  DaysToExpire: nextValue,
                }))
              }
              onClearError={() =>
                setErrors((currentErrors) => ({
                  ...currentErrors,
                  DaysToExpire: "",
                }))
              }
            />

            <FileUploadField
              label="Upload File"
              accept=".xls,.xlsx"
              selectedFile={selectedFile?.size > 0 ? selectedFile : null}
              error={errors.formFile}
              hint="Only Excel files are accepted."
              onDownloadSample={downloadTemplate}
              downloadSampleLabel="Download sample excel file"
              onClearError={clearFileError}
              onSelect={setSelectedFile}
            />

            <div className="new-s2s-form__banner is-neutral">
              <span className="new-s2s-form__banner-icon" aria-hidden="true">
                <FontAwesomeIcon icon={faCircleArrowUp} />
              </span>
              <div>
                <strong>Bulk upload ready</strong>
                <p>
                  Once validated, your file will follow the same authorization
                  and submission flow as the legacy bulk transfer screen.
                </p>
              </div>
            </div>

            <AppButton type="submit" className="new-s2s-form__submit">
              Continue
            </AppButton>
          </form>
        </ComposerCard>
      </ComposerSplit>
    </div>
  );
}
